const API_KEY = process.env.NEXT_PUBLIC_GOOGLE_PLACES_API_KEY;

type PlacePrediction = {
  placeId: string;
  isInterpolated?: boolean;
  text: {
    text: string;
  }
};

type PlaceDetails = {
  id: string;
  addressComponents: Array<{
    longText: string;
    shortText: string;
    types: Array<string>
  }>,
  websiteUri?: string;
};

type PlaceDetailsWithSaneAddress = PlaceDetails & {
  saneAddress: {
    streetNumber: string;
    streetName: string;
    unit: string;
    city: string;
    state: string;
    country: string;
    postalCode: string;
  }
};

const isInterpolated = (prediction: PlacePrediction) => {
  return prediction.placeId.length > 40;
};

const autoComplete = async ({ query }: { query: string }): Promise<Array<PlacePrediction>> => {
  if (!query) return [];

  const result = await fetch(
    'https://places.googleapis.com/v1/places:autocomplete',
    {
      method: 'POST',
      // @ts-ignore
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': API_KEY,
        // 'x-Goog-FieldMask': '*',
        'x-Goog-FieldMask': [
          'suggestions.placePrediction.placeId',
          'suggestions.placePrediction.text',
        ].join(','),
      },
      body: JSON.stringify({
        input: query,
        includedRegionCodes: ['ca', 'us'],
      }),
    },
  );

  const json = await result.json();
  const predictions = json.suggestions
    .map((s: { placePrediction: PlacePrediction }) => s.placePrediction);

  predictions.forEach(
    (p: PlacePrediction) => {
      // eslint-disable-next-line no-param-reassign
      p.isInterpolated = isInterpolated(p);
    },
  );

  return predictions || [];
};

const placeDetails = async ({ placeId }: { placeId: string }): Promise<PlaceDetailsWithSaneAddress | null> => {
  if (!placeId) return null;

  const result = await fetch(
    `https://places.googleapis.com/v1/places/${placeId}`,
    {
      method: 'GET',
      // @ts-ignore
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': API_KEY,
        // 'x-Goog-FieldMask': '*',
        'x-Goog-FieldMask': [
          'id',
          'addressComponents',
          'websiteUri',
        ].join(','),
      },
    },
  );

  const json = await result.json();
  if (!json) return null;

  const saneAddress = getSaneAddress(json);
  return {
    ...json,
    saneAddress,
  };
};

const getSaneAddress = (details: PlaceDetails) => {
  const output = {
    streetNumber: '',
    streetName: '',
    unit: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
  };
  details.addressComponents.forEach((component) => {
    if (component.types.includes('street_number')) {
      output.streetNumber = component.longText;
    } else if (component.types.includes('route')) {
      output.streetName = component.longText;
    } else if (component.types.includes('premise')) {
      output.unit = component.longText;
    } else if (component.types.includes('locality')) {
      output.city = component.longText;
    } else if (component.types.includes('administrative_area_level_1')) {
      output.state = component.shortText;
    } else if (component.types.includes('country')) {
      output.country = component.shortText;
    } else if (component.types.includes('postal_code')) {
      output.postalCode = component.longText;
    }
  });
  return output;
};

export {
  autoComplete,
  placeDetails,
};

export type {
  PlacePrediction,
  PlaceDetails,
};
